import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Link,
  CircularProgress,
  Snackbar,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { resetAddUser } from "../../actions";
import { clearConsumedReferralParam, referralCapturedAtEpochMs } from "../../utils/attribution";
import { getActiveReferralCode, clearReferralCode, REFERRAL_CODE_CAPTURED_AT_KEY } from "../../utils/referral";
import "./login.scss";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const RegisterView = (props) => {
  const [open, setOpen] = useState(false);
  const [emailRegEx, setEmailRegEx] = useState(/^(?!.*(\+))\S+@\S+\.\S+/i);
  const { registerRequest, onRegisterSubmit } = props;
  
  // Add state for real-time password validation
  const [passwordValue, setPasswordValue] = useState("");
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Show/hide toggles so users can verify what they typed against the strict
  // password rules (upper/lower/digit/special) instead of typing blind.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const {
    register,
    watch,
    formState: { errors },
    handleSubmit,
    reset,
    trigger
  } = useForm({
    defaultValues: { roles: 'student' }
  });
  
  // Watch password fields for real-time validation
  const watchedPassword = watch("password");
  const watchedConfirmPassword = watch("confirmPassword");
  
  // Real-time password validation effect
  useEffect(() => {
    if (watchedPassword !== undefined) {
      setPasswordValue(watchedPassword);
      validatePassword(watchedPassword);
    }
  }, [watchedPassword]);
  
  // Real-time confirm password validation effect
  useEffect(() => {
    if (watchedConfirmPassword !== undefined) {
      setConfirmPasswordValue(watchedConfirmPassword);
      validateConfirmPassword(watchedConfirmPassword);
    }
  }, [watchedConfirmPassword, passwordValue]);
  
  // Password validation function
  const validatePassword = (password) => {
    if (!password) {
      setPasswordError("");
      return;
    }
    
    if (password.length < 8) {
      setPasswordError("Password must be of 8 characters");
      return;
    }
    
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()-+])[A-Za-z\d@.#$!%*?&]{8,15}$/;
    if (!passwordPattern.test(password)) {
      setPasswordError("Password should contain 1 Uppercase, 1 Lowercase, 1 digit & 1 special character");
      return;
    }
    
    setPasswordError("");
  };
  
  // Confirm password validation function
  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword) {
      setConfirmPasswordError("");
      return;
    }

    if (passwordValue && confirmPassword !== passwordValue) {
      setConfirmPasswordError("Password does not match");
    } else {
      setConfirmPasswordError("");
    }
  };
  const history = useHistory();
  const dispatch = useDispatch();
  const query = useQuery();
  const token = query.get("token");



  useEffect(() => {
    // Allow + in local
    if (window.location.hostname !== 'qlearn.medlearnity.com' ||
      window.location.hostname !== 'preprodqlearn.medlearnity.com') {
      setEmailRegEx(/\S+@\S+\.\S+/i);
    }
  }, []);

  useEffect(() => {
    if (registerRequest.errorMsg) {
      setTimeout(() => {
        dispatch(resetAddUser());
      }, 3000);
    } else if (registerRequest.addUser) {
      dispatch(resetAddUser());
      setOpen(true);
      reset();
      // setRole('student');

      setTimeout(() => {
        history.push("/login");
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, registerRequest]);

  // const onRoleSelectChange = (value) => {
  //   setRole(value);
  // };

  const goToLogin = (e) => {
    e.stopPropagation();
    history.push("/login");
  };

  const registerUser = (data) => {
    if (data.email.includes('medlearnity.com')) {
      data.roles = 'tutor';
    } else {
      data.roles = 'student';
    }

    data.roles = [data.roles];

    if (token) {
      data.inviteToken = token;
    }

    const referralCode = getActiveReferralCode(window.localStorage);
    if (referralCode) {
      data.referralCode = referralCode;
      // getActiveReferralCode already dropped a stale code, so a capture instant
      // here belongs to a fresh code — forward it (converted to the epoch-millis
      // the backend freshness guard expects), omitting the field when absent or
      // unparseable. clearReferralCode below clears both the code and this key.
      const capturedAtMs = referralCapturedAtEpochMs(
        window.localStorage.getItem(REFERRAL_CODE_CAPTURED_AT_KEY)
      );
      if (capturedAtMs !== null) {
        data.referralCapturedAtEpochMs = capturedAtMs;
      }
      clearReferralCode(window.localStorage);
      clearConsumedReferralParam();
    }

    onRegisterSubmit(data);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Check real-time password validation
    if (passwordValue && passwordError !== "") {
      return;
    }

    if (confirmPasswordValue && confirmPasswordError !== "") {
      return;
    }

    // Trigger form validation and submit if valid
    trigger().then((isValid) => {
      if (isValid) {
        handleSubmit(registerUser)(e);
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box className="login-body">
      <Snackbar open={open} onClose={handleClose} autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Box className="reg-success-alert">
          Registered Successfully. A verification mail has been sent to your email. Please click on that link to verify account.
        </Box>
      </Snackbar>
      <Box component="div" className="app-name">
        QLearn
      </Box>

      <Box component="div" className="login-container">
        <Box component="div" className="error-container">
          {registerRequest.errorMsg}
        </Box>
        <form
          noValidate
          className="login-form"
          onSubmit={handleFormSubmit}
        >
          <TextField
            label="Name"
            {...register("name", { required: true })}
            error={errors.name?.type === "required"}
            helperText={
              errors.name?.type === "required"
                ? "Please enter your name"
                : undefined
            }
          />
          <TextField
            label="Email"
            {...register("email", { required: true, pattern: emailRegEx })}
            error={
              errors.email?.type === "required" ||
              errors.email?.type === "pattern"
            }
            helperText={
              errors.email?.type === "required"
                ? "Email is required"
                : errors.email?.type === "pattern"
                  ? "Please enter a valid email, it should not contain +"
                  : undefined
            }
          // InputLabelProps={{
          //   shrink: watch('email') ? true : false
          // }}
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            {...register("password", {
              required: true,
              minLength: 8,
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()-+])[A-Za-z\d@.#$!%*?&]{8,15}$/
            })}
            error={
              errors.password?.type === "required" ||
              (passwordValue && passwordError !== "")
            }
            helperText={
              errors.password?.type === "required"
                ? "Please enter a password"
                // Show the requirements up front (not only after a failed attempt)
                // so users of this strict-rules form aren't guessing. The special
                // set is the chars the regex actually accepts (intersection of its
                // lookahead and character class), so the hint can't mislead.
                : passwordError || "8–15 characters, with an uppercase, lowercase, number & a special character (! @ # $ % * &)"
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((show) => !show)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            {...register("confirmPassword", {
              required: true,
              validate: (value) => value === watch("password"),
            })}
            error={
              errors.confirmPassword?.type === "required" ||
              (confirmPasswordValue && confirmPasswordError !== "")
            }
            helperText={
              errors.confirmPassword?.type === "required"
                ? "Please enter a password"
                : confirmPasswordError
                  ? confirmPasswordError
                  // Positive confirmation once the two passwords match, so users
                  // aren't left guessing whether the retype was accepted.
                  : (confirmPasswordValue && confirmPasswordValue === passwordValue)
                    ? <span style={{ color: '#2e7d32' }}><span aria-hidden="true">✓ </span>Passwords match</span>
                    : undefined
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                    onClick={() => setShowConfirmPassword((show) => !show)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="register-btn"
            disabled={registerRequest.loading}
          >
            Register
            {registerRequest.loading && (
              <CircularProgress size={24} className="loader" />
            )}
          </Button>
        </form>
        Already a user? &nbsp;
        <Link variant="body2" rel="noopener" onClick={goToLogin}>
          Sign In
        </Link>
      </Box>
    </Box>
  );
};

export default RegisterView;
